// Single source of truth for all routes. Consumed by NotFound, PhasePlaceholder, and the
// Playwright clickability spider. Every new route MUST be added here.

export const MODULES = {
  crm: {
    phase: 2,
    name: "CRM Pipeline",
    description: "Lead capture → qualification → site visit → opportunity → estimate → proposal → contract → deposit.",
    liveEndpoints: [
      "GET  /api/leads",
      "POST /api/leads/:id/convert",
      "GET  /api/opportunities",
      "PATCH /api/opportunities/:id",
      "GET  /api/accounts",
      "GET  /api/contacts",
      "GET  /api/properties",
      "GET  /api/site-visits",
    ],
    liveUiRoutes: [
      { path: "/crm/leads", label: "Leads" },
      { path: "/crm/opportunities", label: "Opportunities" },
    ],
  },
  estimating: {
    phase: 2,
    name: "Estimating & Bidding",
    description: "Line-item costing with server-authoritative totals, dollar-band approval workflow.",
    liveEndpoints: [
      "GET  /api/estimates",
      "POST /api/estimates",
      "PATCH /api/estimates/:id",
      "POST /api/estimates/:id/submit-for-approval",
      "POST /api/estimates/:id/mark-approved",
    ],
    liveUiRoutes: [{ path: "/crm/estimates", label: "Estimates" }],
  },
  proposals: {
    phase: 2,
    name: "Proposals",
    description: "Client-facing scope/price document derived from approved estimates. Public token viewer.",
    liveEndpoints: [
      "GET  /api/proposals",
      "POST /api/proposals",
      "POST /api/proposals/:id/send",
      "POST /api/proposals/public/view/:token",
      "POST /api/proposals/public/decide/:token",
    ],
    liveUiRoutes: [{ path: "/crm/proposals", label: "Proposals" }],
  },
  contracts: {
    phase: 2,
    name: "Contracts & E-Sign",
    description: "Drafted from accepted proposals. Internal placeholder e-signature until DocuSign/Dropbox Sign is Connected.",
    liveEndpoints: [
      "GET  /api/contracts",
      "POST /api/contracts",
      "POST /api/contracts/:id/send-for-signature",
      "GET  /api/contracts/public/sign/:token",
      "POST /api/contracts/public/sign/:token",
    ],
    liveUiRoutes: [{ path: "/crm/contracts", label: "Contracts" }],
  },
  deposits: {
    phase: 2,
    name: "Deposits & Payments",
    description: "Payment link generation for signed contracts. Online payments are not enabled (Stripe externally blocked).",
    liveEndpoints: [
      "GET  /api/deposits",
      "POST /api/deposits",
      "POST /api/deposits/:id/mark-paid-manual",
      "GET  /api/deposits/public/:token",
    ],
    liveUiRoutes: [{ path: "/crm/deposits", label: "Deposits" }],
  },
  project_record: { phase: 3, name: "Project Record", description: "Job costing, schedule, RFIs, submittals, change orders." },
  sub_vendor_network: { phase: 3, name: "Sub / Vendor Network", description: "Subcontractor & vendor directory with COI, W-9, prequal, and scorecards." },
  field_ops: { phase: 4, name: "Field Operations & Daily Logs", description: "Daily logs, punch list, safety, photos. Timecards are not implemented." },
  financials: { phase: 5, name: "Financials & Accounting", description: "Contracts, pay applications, AP, job costing, lien waivers. Bank feeds and QuickBooks/Xero sync are externally blocked." },
  invoicing: { phase: 5, name: "Invoicing", description: "AIA-style progress billing linked to contract schedules." },
  client_portal: { phase: 6, name: "Client Portal", description: "External-facing project transparency for owners and developers." },
  property_management: { phase: 7, name: "Property Management", description: "Unit-level lease, maintenance, and work-order coordination." },
  construction_iq: { phase: 8, name: "Construction IQ", description: "Owner priority reasoning, executive AI briefs, risk radar." },
  jurisdiction_intel: { phase: 9, name: "Jurisdiction Intel", description: "Permit portal linkage across PA, GA, DE, MD, VA jurisdictions." },
  marketing_studio: { phase: 10, name: "Marketing Studio", description: "Public site, CRM landing, campaign attribution." },
  mfa: { phase: 10, name: "MFA & Advanced Hardening", description: "TOTP / WebAuthn enrollment, session risk scoring, IP allow-lists." },
};

export const ROUTES = [
  { path: "/", title: "Landing", section: "Public", public: true },
  { path: "/login", title: "Sign in", section: "Public", public: true },
  { path: "/auth/rotate", title: "Rotate Password", section: "Public", public: true },
  { path: "/rotate-password", title: "Rotate Password", section: "Public", public: true },
  { path: "/quote", title: "Request a Quote", section: "Public", public: true },

  { path: "/owner", title: "Owner Command Center", section: "Executive Command", testId: "route-owner" },
  { path: "/help", title: "Help Center & Tutorials", section: "Executive Command", testId: "route-help" },
  { path: "/admin/portal-switch", title: "Portal Impersonation", section: "Executive Command" },

  { path: "/admin/tenants", title: "Tenants Directory", section: "Governance & Admin" },
  { path: "/admin/users", title: "Users & Access", section: "Governance & Admin" },
  { path: "/admin/roles", title: "Role Matrix", section: "Governance & Admin" },
  { path: "/admin/authority", title: "Authority Matrix", section: "Governance & Admin" },
  { path: "/admin/approvals", title: "Approvals Inbox", section: "Governance & Admin" },
  { path: "/admin/audit", title: "Audit Ledger", section: "Governance & Admin" },
  { path: "/admin/feature-requests", title: "Early-Access Requests", section: "Governance & Admin" },

  { path: "/admin/documents", title: "Document Vault", section: "Operations & Assets" },
  { path: "/admin/credentials", title: "Credentials & Compliance", section: "Operations & Assets" },
  { path: "/admin/integrations", title: "Integration Health", section: "Operations & Assets" },
  { path: "/admin/notifications/outbox", title: "Notifications Outbox", section: "Operations & Assets" },
  { path: "/admin/feature-flags", title: "Feature Flags", section: "Operations & Assets" },
  { path: "/admin/trade-catalog", title: "Trade & Sector Catalog", section: "Operations & Assets" },

  // Phase 2 (backend live, UI in progress)
  { path: "/crm/leads", title: "CRM · Leads", section: "Sales Pipeline", phase: 2 },
  { path: "/crm/opportunities", title: "CRM · Opportunities", section: "Sales Pipeline", phase: 2 },
  { path: "/crm/estimates", title: "Estimating · Estimates", section: "Sales Pipeline", phase: 2 },
  { path: "/crm/proposals", title: "Proposals", section: "Sales Pipeline", phase: 2 },
  { path: "/crm/contracts", title: "Contracts", section: "Sales Pipeline", phase: 2 },
  { path: "/crm/contract-templates", title: "Contract templates", section: "Sales Pipeline", phase: 2 },
  { path: "/pm/my-tasks", title: "My tasks", section: "Projects", phase: 3 },
  { path: "/field", title: "Field home", section: "Projects", phase: 4 },
  { path: "/field/reports", title: "Daily reports", section: "Projects", phase: 4 },
  { path: "/crm/deposits", title: "Deposits", section: "Sales Pipeline", phase: 2 },

  // Phase placeholders (later phases)
  { path: "/phase/project_record", title: "Project Record", section: "Modules", phase: 3, placeholder: true },
  { path: "/phase/sub_vendor_network", title: "Sub / Vendor Network", section: "Modules", phase: 3, placeholder: true },
  { path: "/phase/field_ops", title: "Field Operations & Daily Logs", section: "Modules", phase: 4, placeholder: true },
  { path: "/phase/financials", title: "Financials & AR/AP", section: "Modules", phase: 5, placeholder: true },
  { path: "/phase/invoicing", title: "Invoicing", section: "Modules", phase: 5, placeholder: true },
  { path: "/phase/client_portal", title: "Client Portal", section: "Modules", phase: 6, placeholder: true },
  { path: "/phase/property_management", title: "Property Management", section: "Modules", phase: 7, placeholder: true },
  { path: "/phase/construction_iq", title: "Construction IQ", section: "Modules", phase: 8, placeholder: true },
  { path: "/phase/jurisdiction_intel", title: "Jurisdiction Intel", section: "Modules", phase: 9, placeholder: true },
  { path: "/phase/marketing_studio", title: "Marketing Studio", section: "Modules", phase: 10, placeholder: true },
  { path: "/phase/mfa", title: "MFA & Advanced Hardening", section: "Modules", phase: 10, placeholder: true },
];

// -------- fuzzy matching --------
function levenshtein(a, b) {
  const m = a.length, n = b.length;
  if (m === 0) return n;
  if (n === 0) return m;
  const dp = Array.from({ length: m + 1 }, (_, i) => [i, ...Array(n).fill(0)]);
  for (let j = 0; j <= n; j++) dp[0][j] = j;
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      dp[i][j] = Math.min(dp[i - 1][j] + 1, dp[i][j - 1] + 1, dp[i - 1][j - 1] + cost);
    }
  }
  return dp[m][n];
}

export function suggestRoutes(inputPath, limit = 3) {
  const p = (inputPath || "").toLowerCase().replace(/^\/+/, "").replace(/\/+$/, "");
  const scored = ROUTES
    .filter((r) => !r.path.includes(":"))
    .map((r) => {
      const target = r.path.toLowerCase().replace(/^\/+/, "");
      const d = levenshtein(p, target);
      const contains = target.includes(p) || p.includes(target) ? -3 : 0;
      const titleMatch = r.title.toLowerCase().includes(p) ? -2 : 0;
      return { route: r, score: d + contains + titleMatch };
    });
  scored.sort((a, b) => a.score - b.score);
  return scored.slice(0, limit).filter((s) => s.score <= 8).map((s) => s.route);
}
